// SubtitlesConfigModal.tsx
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Modal, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import SubtitlesEditor from '../../subtitles/subtitles-editor';
import { baseSubtitles, SubtitlesConfig } from '@monorepo/types';
import SubtitleStyleModal, {
  SubtitleStyleModalActions,
} from './subtitles-preset-modal';
import { BlockOutlined } from '@ant-design/icons';

export interface SubtitlesConfigModalActions {
  open: () => void;
}

interface Props {
  form: UseFormReturn<any>;
}

const SubtitlesModal = forwardRef<SubtitlesConfigModalActions, Props>(
  ({ form }, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const styleModalRef = useRef<SubtitleStyleModalActions>(null);

    const subtitlesConfig = form.getValues('subtitlesConfig') || baseSubtitles;

    useEffect(() => {
      form.setValue('subtitlesConfig', subtitlesConfig || baseSubtitles, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }, []);

    useImperativeHandle(ref, () => ({
      open: () => {
        setIsOpen(true);
      },
    }));

    const handleCancel = () => {
      setIsOpen(false);
    };

    const handleSave = (config: SubtitlesConfig) => {
      form.setValue('subtitlesConfig', config, {
        shouldDirty: true,
        shouldValidate: true,
      });
    };

    const openStyleModal = () => {
      styleModalRef.current?.open();
    };

    const onPresetApply = (preset: SubtitlesConfig) => {
      handleSave(preset);
    };

    return (
      <Modal
        title={t('subtitlesModal.title')}
        open={isOpen}
        onCancel={handleCancel}
        width={800}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            {t('common.close')}
          </Button>,
        ]}
      >
        <Button
          style={{ width: '100%', marginBottom: '15px' }}
          icon={<BlockOutlined />}
          onClick={openStyleModal}
        >
          {t('subtitlesModal.browsePresets')}
        </Button>
        <SubtitleStyleModal onSave={onPresetApply} ref={styleModalRef} />
        <SubtitlesEditor
          subtitlesConfig={subtitlesConfig}
          onUpdate={handleSave}
        />
      </Modal>
    );
  }
);

export default SubtitlesModal;
